import styled from 'styled-components';
import { theme } from '../theme';

// TikTok-style Top Navigation Bar
export const ProfileTopNavContainer = styled.div<{ $menuOpen?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* While the dropdown is open, lift this bar's stacking context above the
     mobile backdrop (z-index 1000) so the menu — which lives inside this
     fixed, z-indexed container — is actually clickable. Otherwise the bar
     traps the menu below the backdrop and taps hit the backdrop instead. */
  z-index: ${props => (props.$menuOpen ? 1100 : 900)};
  background: var(--bg-card);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${theme.colors.border.light};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: ${theme.shadows.light};
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    min-height: 56px;
  }

  /* Make room for the persistent desktop sidebar so this bar's own controls
     (e.g. the hamburger menu, which sits flush left) aren't covered by it. */
  @media (min-width: calc(${theme.breakpoints.tablet} + 1px)) {
    left: 88px;
    width: calc(100% - 88px);
  }
`;

export const MenuWrapper = styled.div`
  position: relative;
`;

export const ProfileTopNavLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.sm};
  }
`;

export const ProfileTopNavRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.sm};
  }
`;

export const ProfileTopNavButton = styled.button<{ $isPremium?: boolean; $isActive?: boolean }>`
  background: transparent;
  border: none;
  padding: ${theme.spacing.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  color: ${props => props.$isPremium ? '#FFD700' : theme.colors.text.primary};
  min-width: 44px; /* Minimum touch target size for iOS */
  min-height: 44px;
  
  &:hover {
    background: ${theme.colors.background.secondary};
  }
  
  &:active {
    transform: scale(0.95);
    background: ${theme.colors.background.tertiary};
  }
  
  /* Active indicator */
  ${props => props.$isActive && `
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.primary};
  `}
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs};
    min-width: 48px; /* Android recommended touch target */
    min-height: 48px;
  }
  
  @media (max-width: 380px) {
    min-width: 44px;
    min-height: 44px;
    padding: 6px;
  }
`;

export const ProfileTopNavIcon = styled.span<{ $size?: string; $isPremium?: boolean }>`
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  color: inherit;
  
  svg {
    width: ${props => props.$size === '1.8rem' ? '28px' : '24px'};
    height: ${props => props.$size === '1.8rem' ? '28px' : '24px'};
    color: inherit;
  }
  
  ${props => props.$isPremium && `
    svg {
      background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      filter: drop-shadow(0 0 4px rgba(255, 215, 0, 0.3));
    }
  `}
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    svg {
      width: ${props => props.$size === '1.8rem' ? '24px' : '20px'};
      height: ${props => props.$size === '1.8rem' ? '24px' : '20px'};
    }
  }
  
  @media (max-width: 380px) {
    svg {
      width: ${props => props.$size === '1.8rem' ? '22px' : '18px'};
      height: ${props => props.$size === '1.8rem' ? '22px' : '18px'};
    }
  }
`;

export const ProfileTopNavTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
  flex: 1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 ${theme.spacing.xs};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
    max-width: 180px;
  }
  
  @media (max-width: 400px) {
    font-size: 0.9rem;
    max-width: 140px;
  }
  
  @media (max-width: 350px) {
    font-size: 0.85rem;
    max-width: 120px;
  }
`;

// Notification Badge
export const NotificationBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  background: ${theme.colors.error};
  border-radius: 50%;
  border: 2px solid ${theme.colors.background.card};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 6px;
    height: 6px;
    top: 2px;
    right: 2px;
  }
`;

// Premium Badge (for premium button)
export const PremiumBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 4px;
  border-radius: ${theme.borderRadius.sm};
  line-height: 1;
  box-shadow: 0 2px 6px rgba(255, 165, 0, 0.4);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.55rem;
    padding: 1px 3px;
  }
`;

// Hamburger Menu Styles
export const HamburgerIcon = styled.div<{ $isOpen?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  cursor: pointer;
  user-select: none;
  
  span {
    display: block;
    width: 100%;
    height: 2.5px;
    background: ${theme.colors.text.primary};
    border-radius: 2px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    ${props => props.$isOpen && `
      &:nth-child(1) {
        transform: translateY(7.75px) rotate(45deg);
      }
      
      &:nth-child(2) {
        opacity: 0;
        transform: scale(0);
      }
      
      &:nth-child(3) {
        transform: translateY(-7.75px) rotate(-45deg);
      }
    `}
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 22px;
    height: 16px;
    
    span {
      height: 2.5px;
    }
    
    ${props => props.$isOpen && `
      span {
        &:nth-child(1) {
          transform: translateY(6.75px) rotate(45deg);
        }
        
        &:nth-child(3) {
          transform: translateY(-6.75px) rotate(-45deg);
        }
      }
    `}
  }
`;

// Dropdown Menu (for hamburger)
export const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 64px;
  left: ${theme.spacing.md};
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.medium};
  min-width: 220px;
  max-width: 280px;
  padding: ${theme.spacing.sm} 0;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  z-index: 1001;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid ${theme.colors.border.light};
  
  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (min-width: calc(${theme.breakpoints.tablet} + 1px)) {
    left: calc(88px + ${theme.spacing.md});
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 56px;
    left: ${theme.spacing.sm};
    min-width: 200px;
    max-width: 260px;
    max-height: calc(100vh - 70px - 70px); /* Account for top nav and bottom nav */
    border-radius: ${theme.borderRadius.md};
  }
  
  @media (max-width: 380px) {
    min-width: 180px;
    max-width: 240px;
  }
`;

export const DropdownMenuItem = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  min-height: 48px; /* Touch-friendly height */
  user-select: none;
  
  &:hover {
    background: ${theme.colors.background.secondary};
  }
  
  &:active {
    background: ${theme.colors.background.tertiary};
    transform: scale(0.98);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md} ${theme.spacing.md};
    font-size: 0.9rem;
    min-height: 52px; /* Slightly taller on mobile for easier tapping */
  }
  
  @media (max-width: 380px) {
    padding: ${theme.spacing.sm} ${theme.spacing.sm};
    font-size: 0.85rem;
  }
`;

export const DropdownMenuIcon = styled.span`
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  
  svg {
    width: 18px;
    height: 18px;
    color: inherit;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const DropdownMenuText = styled.span`
  flex: 1;
`;

export const DropdownMenuDivider = styled.div`
  height: 1px;
  background: ${theme.colors.border.light};
  margin: ${theme.spacing.xs} 0;
`;

export const NotificationToast = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${theme.colors.background.card};
  color: ${theme.colors.text.primary};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.full};
  box-shadow: ${theme.shadows.medium};
  border: 1px solid ${theme.colors.border.light};
  z-index: 2000;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: 500;
  font-size: 0.9rem;
  opacity: ${props => props.$isVisible ? 1 : 0};
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  pointer-events: none;

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 70px;
    font-size: 0.85rem;
  }
`;

// Backdrop overlay for mobile dropdown menu
export const DropdownBackdrop = styled.div<{ $isOpen: boolean }>`
  display: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 1000;
    animation: ${props => props.$isOpen ? 'fadeIn 0.2s ease' : 'none'};
    
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
  }
`;

